import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status"]

  connect() {
    this.element.querySelectorAll(".badge-aq-emoticon-list").forEach((list) => {
      if (list.dataset.selectionMode === "single") {
        const inputs = this.inputsFor(list)
        const selectedInput = inputs.find((input) => input.checked)
        const autoSelect = list.dataset.autoSelect !== "false"

        if (selectedInput) {
          this.syncCheckedClasses(list)
        } else if (autoSelect && inputs[0]) {
          inputs[0].checked = true
          this.syncCheckedClasses(list)
        }
      } else {
        const buttons = this.buttonsFor(list)
        const selectedButton = buttons.find((button) => button.getAttribute("aria-pressed") === "true")

        if (selectedButton) {
          this.updateStatus(list)
        }
      }

      this.updateStatus(list)
    })
  }

  selectRadio(event) {
    const input = event.currentTarget
    const list = input.closest(".badge-aq-emoticon-list")
    if (!list) return

    this.syncCheckedClasses(list)
    this.updateStatus(list)
    this.dispatchSelected(input, list)
  }

  toggle(event) {
    const button = event.currentTarget
    const list = button.closest(".badge-aq-emoticon-list")

    if (list?.dataset.selectionMode === "single") {
      return
    }

    const nextState = button.getAttribute("aria-pressed") !== "true"
    button.setAttribute("aria-pressed", nextState.toString())
    this.updateStatus(list)
  }

  keydown(event) {
    const list = event.currentTarget.closest(".badge-aq-emoticon-list")

    if (!list || list.dataset.selectionMode === "single") {
      return
    }

    const buttons = this.buttonsFor(list)
    const currentIndex = buttons.indexOf(event.currentTarget)

    if (currentIndex < 0) {
      return
    }

    let nextIndex = currentIndex

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % buttons.length
        break
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = buttons.length - 1
        break
      case " ":
      case "Enter":
        event.preventDefault()
        this.toggle(event)
        return
      default:
        return
    }

    event.preventDefault()
    buttons[nextIndex].focus()
  }

  inputsFor(list) {
    return Array.from(list.querySelectorAll(".badge-aq-emoticon-input[type='radio']"))
  }

  buttonsFor(list) {
    return Array.from(list.querySelectorAll("button.badge-aq-emoticon"))
  }

  labelForInput(input) {
    if (!(input instanceof HTMLInputElement)) return null

    const labels = input.labels
    if (labels && labels.length > 0) return labels[0]

    const next = input.nextElementSibling
    if (next instanceof HTMLLabelElement) return next

    return null
  }

  syncCheckedClasses(list) {
    this.inputsFor(list).forEach((input) => {
      const label = this.labelForInput(input)
      if (label) {
        label.classList.toggle("badge-aq-emoticon--checked", input.checked)
      }
    })
  }

  dispatchSelected(input, list) {
    const label = this.labelForInput(input)
    this.dispatch("selected", { detail: { label, input, list }, bubbles: true })
  }

  accessibleName(control) {
    if (control instanceof HTMLInputElement) {
      const ariaLabel = control.getAttribute("aria-label")?.trim()
      if (ariaLabel) return ariaLabel

      const optionLabel = control.dataset.optionLabel?.trim()
      if (optionLabel) return optionLabel
    }

    if (control instanceof HTMLElement) {
      const ariaLabel = control.getAttribute("aria-label")?.trim()
      if (ariaLabel) return ariaLabel

      const optionLabel = control.dataset.optionLabel?.trim()
      if (optionLabel) return optionLabel
    }

    return "Option"
  }

  statusFor(list) {
    return this.statusTargets.find((target) => target.id === list.dataset.statusId)
  }

  updateStatus(list) {
    const status = this.statusFor(list)

    if (!status) {
      return
    }

    if (list.dataset.selectionMode === "single") {
      const inputs = this.inputsFor(list)
      const selectedInput = inputs.find((input) => input.checked)

      if (!selectedInput) {
        status.textContent = "No option selected"
        return
      }

      const name = this.accessibleName(selectedInput)
      const position = inputs.indexOf(selectedInput) + 1
      status.textContent = `${name} selected, ${position} of ${inputs.length}`
      return
    }

    const buttons = this.buttonsFor(list)
    const selectedLabels = buttons
      .filter((button) => button.getAttribute("aria-pressed") === "true")
      .map((button) => button.dataset.optionLabel)

    status.textContent = selectedLabels.length > 0 ? `${selectedLabels.join(", ")} selected` : "No option selected"
  }
}
